import type { FilterValueStatus } from "@octopusdeploy/octopus-server-client";
import { FilterValueStatusValues } from "@octopusdeploy/octopus-server-client";
import { createQueryParam, numberQueryParam, optionalStringQueryParam, useQueryStringParams } from "@octopusdeploy/portal-routes";
import { useMemo } from "react";
import type { ProjectTenantVariablesFilterState } from "~/areas/projects/components/Variables/TenantVariables/FiltersBar";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
export function useCommonTemplatesFilterState(): [
    ProjectTenantVariablesFilterState,
    (filter: ProjectTenantVariablesFilterState) => void
] {
    const searchParameter = optionalStringQueryParam("search");
    const tenantParameter = optionalStringQueryParam("tenant");
    const excludedTenantParameter = optionalStringQueryParam("excludedTenant");
    const tagsParameter = optionalStringQueryParam("tags");
    const excludedTagsParameter = optionalStringQueryParam("excludedTags");
    const valueStatusParameter = createQueryParam("valueStatus", validateValueStatus, (v) => v);
    const pageNumberParameter = numberQueryParam("pageNumber");
    const libraryVariableSetIdParameter = optionalStringQueryParam("libraryVariableSetId");
    const [filter, setFilter] = useQueryStringParams([searchParameter, tenantParameter, excludedTenantParameter, tagsParameter, excludedTagsParameter, valueStatusParameter, pageNumberParameter, libraryVariableSetIdParameter]);
    function setProjectTenantVariablesFilterState(filter: ProjectTenantVariablesFilterState) {
        const customFieldsEnabled = isFeatureToggleEnabled("ExtendedTagSetsFeatureToggle");
        setFilter({
            search: filter.filterByName,
            tenant: filter.filterByTenant,
            excludedTenant: filter.filterByExcludedTenant,
            tags: customFieldsEnabled && filter.filterByCustomFields.length > 0 ? filter.filterByCustomFields.join(",") : filter.filterByTags.length > 0 ? filter.filterByTags.join(",") : undefined,
            excludedTags: customFieldsEnabled && filter.filterByExcludedCustomFields.length > 0 ? filter.filterByExcludedCustomFields.join(",") : filter.filterByExcludedTags.length > 0 ? filter.filterByExcludedTags.join(",") : undefined,
            valueStatus: filter.filterByValueStatus,
            pageNumber: filter.pageNumber,
            libraryVariableSetId: filter.filterByLibraryVariableSetId,
        });
    }
    const customFieldsEnabled = isFeatureToggleEnabled("ExtendedTagSetsFeatureToggle");
    const projectTenantVariablesFilterState = useMemo((): ProjectTenantVariablesFilterState => ({
        filterByName: filter.search,
        filterByEnvironment: undefined,
        filterByExcludedEnvironment: undefined,
        filterByTenant: filter.tenant,
        filterByExcludedTenant: filter.excludedTenant,
        filterByTags: customFieldsEnabled ? [] : filter.tags ? filter.tags.split(",") : [],
        filterByExcludedTags: customFieldsEnabled ? [] : filter.excludedTags ? filter.excludedTags.split(",") : [],
        filterByCustomFields: customFieldsEnabled && filter.tags ? filter.tags.split(",") : [],
        filterByExcludedCustomFields: customFieldsEnabled && filter.excludedTags ? filter.excludedTags.split(",") : [],
        filterByValueStatus: filter.valueStatus,
        filterByTemplateId: undefined,
        filterByLibraryVariableSetId: filter.libraryVariableSetId,
        pageNumber: filter.pageNumber ?? 1,
    }), [filter.search, filter.tenant, filter.excludedTenant, filter.tags, filter.excludedTags, filter.valueStatus, filter.pageNumber, filter.libraryVariableSetId, customFieldsEnabled]);
    return [projectTenantVariablesFilterState, setProjectTenantVariablesFilterState];
}
function validateValueStatus(valueStatus: string | undefined): FilterValueStatus | undefined {
    if (valueStatus === undefined)
        return undefined;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const possibleStatus = valueStatus as FilterValueStatus;
    return Object.values(FilterValueStatusValues).includes(possibleStatus) ? possibleStatus : undefined;
}
